import { Alert, Button, Card, Form, Select, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationSessionSelect } from '@/pages/education/components/EducationLookupSelects';

type Row = {
  id: string;
  admission_no: string;
  first_name: string;
  last_name?: string | null;
  pen_id?: string | null;
  apaar_id?: string | null;
  session_code?: string | null;
};

export default function IdsReadinessPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.ids.manage') || user?.permissions?.includes('education.admin')
  );
  const [idKind, setIdKind] = useState<string | undefined>();
  const [sessionId, setSessionId] = useState<string | undefined>();

  const list = useQuery({
    queryKey: ['education-id-readiness', idKind, sessionId],
    queryFn: async () => {
      const res = await api.get<Row[]>('/education/ids/readiness', {
        params: { id_kind: idKind, session_id: sessionId },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  return (
    <EducationPageChrome
      title={t('idReadiness')}
      description={t('idReadinessIntro')}
      extra={<Button onClick={() => navigate('/education/ids')}>{t('ids')}</Button>}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('idsExternalHint')} />
        <Card size="small">
          <Form layout="inline" onFinish={() => list.refetch()}>
            <Form.Item label={t('idKind')}>
              <Select
                allowClear
                style={{ minWidth: 140 }}
                value={idKind}
                onChange={(v) => setIdKind(v || undefined)}
                options={[
                  { value: 'pen', label: 'PEN' },
                  { value: 'apaar', label: 'APAAR' },
                  { value: 'both', label: t('eitherMissing') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('session')}>
              <EducationSessionSelect
                allowClear
                style={{ minWidth: 200 }}
                value={sessionId}
                onChange={(v) => setSessionId((Array.isArray(v) ? v[0] : v) || undefined)}
              />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={list.isFetching}>
              {t('filter')}
            </Button>
          </Form>
        </Card>
        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={list.data ?? []}
            pagination={{ pageSize: 50 }}
            columns={[
              { title: t('admissionNo'), dataIndex: 'admission_no', width: 120 },
              {
                title: t('student'),
                render: (_: unknown, row: Row) =>
                  [row.first_name, row.last_name].filter(Boolean).join(' '),
              },
              {
                title: 'PEN',
                dataIndex: 'pen_id',
                render: (v: string | null | undefined) => (v ? <Tag color="green">{v}</Tag> : <Tag>missing</Tag>),
              },
              {
                title: 'APAAR',
                dataIndex: 'apaar_id',
                render: (v: string | null | undefined) => (v ? <Tag color="green">{v}</Tag> : <Tag>missing</Tag>),
              },
              {
                title: t('actions'),
                width: 160,
                render: (_: unknown, row: Row) =>
                  canManage ? (
                    <Button
                      size="small"
                      onClick={() =>
                        navigate('/education/ids/workflows/new', {
                          state: { student_id: row.id },
                        })
                      }
                    >
                      {t('newIdWorkflow')}
                    </Button>
                  ) : null,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
